import { useState } from "react";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import { SquaresFour, CalendarBlank, Wrench, Play, Sun, Moon, FileText, BookmarksSimple, FolderOpen, GoogleLogo, SignOut, FilmStrip, ChatCircleDots, Tray, CaretDown, List, X, MagicWand } from "@phosphor-icons/react";
import { useTheme } from "@/hooks/useTheme";
import { useAuth, startGoogleLogin } from "@/context/AuthContext";
import { TOOLS, TOOL_GROUPS } from "@/lib/tools";

const navCls = ({ isActive }) =>
  `flex items-center gap-3 px-3 py-2.5 rounded-[10px] text-sm font-medium transition-colors ${
    isActive ? "bg-[var(--glow)] text-white" : "text-[var(--muted)] hover:text-[var(--text)] hover:bg-[var(--card)]"
  }`;

const CREATOR_LINKS = [
  { to: "/dashboard", label: "Dashboard", icon: SquaresFour, testId: "nav-dashboard" },
  { to: "/studio", label: "Studio", icon: MagicWand, testId: "nav-studio" },
  { to: "/calendar", label: "Calendar & Scripts", icon: CalendarBlank, testId: "nav-calendar" },
  { to: "/ideas", label: "Idea Board", icon: BookmarksSimple, testId: "nav-idea-board" },
  { to: "/media", label: "Media Library", icon: FolderOpen, testId: "nav-media-library" },
  { to: "/reviews", label: "Video Reviews", icon: FilmStrip, testId: "nav-video-reviews" },
  { to: "/contact", label: "Contact Us", icon: ChatCircleDots, testId: "nav-contact" },
];

const NavContent = ({ isAdmin, onNavigate }) => {
  const location = useLocation();
  const [openGroups, setOpenGroups] = useState({});
  const isOpen = (key) => openGroups[key] ?? TOOLS.some((t) => t.group === key && location.pathname === t.path);
  const toggle = (key) => setOpenGroups((o) => ({ ...o, [key]: !isOpen(key) }));

  return (
    <>
      <nav className="space-y-1">
        {CREATOR_LINKS.map((l) => (
          <NavLink key={l.to} to={l.to} data-testid={l.testId} className={navCls} onClick={onNavigate}>
            <l.icon size={18} weight="duotone" /> {l.label}
          </NavLink>
        ))}
        {isAdmin && (
          <NavLink to="/admin/inbox" data-testid="nav-admin-inbox" className={navCls} onClick={onNavigate}>
            <Tray size={18} weight="duotone" /> Admin Inbox
          </NavLink>
        )}
      </nav>
      <p className="text-xs uppercase tracking-[0.2em] text-[var(--muted)] px-3 mt-7 mb-2">Free Tools</p>
      <div className="space-y-1 pb-3">
        {TOOL_GROUPS.map((g) => {
          const groupTools = TOOLS.filter((t) => t.group === g.key);
          const open = isOpen(g.key);
          return (
            <div key={g.key}>
              <button
                data-testid={`tool-group-${g.key}`}
                onClick={() => toggle(g.key)}
                className="w-full flex items-center justify-between px-3 py-2 rounded-[10px] text-sm font-semibold text-[var(--text)] hover:bg-[var(--card)] transition-colors"
              >
                <span className="flex items-center gap-3"><Wrench size={16} weight="duotone" className="text-[var(--glow)]" /> {g.label}</span>
                <CaretDown size={13} weight="bold" className={`transition-transform ${open ? "rotate-180" : ""}`} />
              </button>
              {open && (
                <div className="mt-0.5 ml-3 pl-3 border-l border-[var(--border-c)] space-y-0.5">
                  {groupTools.map((t) => (
                    <NavLink key={t.slug} to={t.path} data-testid={`nav-${t.slug}`} className={navCls} onClick={onNavigate}>
                      <t.icon size={17} weight="duotone" /> {t.name}
                    </NavLink>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </>
  );
};

const AccountFooter = ({ user, logout, theme, toggleTheme, suffix = "" }) => (
  <div className="border-t border-[var(--border-c)] pt-3 mt-3">
    {user ? (
      <div className="flex items-center gap-2.5 px-3 py-2" data-testid={`user-profile${suffix}`}>
        {user.picture ? (
          <img src={user.picture} alt="" className="w-8 h-8 rounded-full" referrerPolicy="no-referrer" />
        ) : (
          <span className="w-8 h-8 rounded-full bg-[var(--glow)]/20 flex items-center justify-center text-xs font-bold text-[var(--glow)]">{(user.name || "?")[0]}</span>
        )}
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold truncate">{user.name}</p>
          <p className="text-[10px] text-[var(--muted)] truncate">{user.email}</p>
        </div>
        <button data-testid={`logout-btn${suffix}`} onClick={logout} title="Sign out" className="text-[var(--muted)] hover:text-[var(--coral)] transition-colors">
          <SignOut size={17} />
        </button>
      </div>
    ) : (
      <button
        data-testid={`login-btn${suffix}`}
        onClick={startGoogleLogin}
        className="w-full flex items-center justify-center gap-2 px-3 py-2.5 rounded-[10px] bg-[var(--glow)] text-white text-sm font-semibold hover:bg-[#6a4ce6] transition-colors"
      >
        <GoogleLogo size={16} weight="bold" /> Sign in with Google
      </button>
    )}
    <button
      data-testid={`theme-toggle${suffix}`}
      onClick={toggleTheme}
      className="mt-1 w-full flex items-center gap-3 px-3 py-2.5 rounded-[10px] text-sm font-medium text-[var(--muted)] hover:text-[var(--text)] hover:bg-[var(--card)] transition-colors"
    >
      {theme === "dark" ? <Sun size={18} weight="duotone" /> : <Moon size={18} weight="duotone" />}
      {theme === "dark" ? "Light mode" : "Dark mode"}
    </button>
  </div>
);

const Logo = () => (
  <NavLink to="/" data-testid="nav-logo" className="flex items-center gap-2.5 px-2">
    <span className="w-9 h-9 rounded-[10px] bg-[var(--coral)] flex items-center justify-center">
      <Play size={16} color="#fff" weight="fill" />
    </span>
    <span className="font-display font-black text-lg tracking-tight">VidhFlow</span>
  </NavLink>
);

export const Shell = () => {
  const { theme, toggle } = useTheme();
  const { user, logout } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);

  return (
    <div className="min-h-screen flex">
      <aside className="hidden lg:flex flex-col w-64 fixed inset-y-0 border-r border-[var(--border-c)] p-5">
        <div className="mb-7"><Logo /></div>
        <div className="flex-1 min-h-0 overflow-y-auto -mx-1 px-1" data-testid="sidebar-scroll-area">
          <NavContent isAdmin={user?.isAdmin} />
        </div>
        <AccountFooter user={user} logout={logout} theme={theme} toggleTheme={toggle} />
      </aside>

      <main className="flex-1 min-w-0 lg:ml-64 pb-24 lg:pb-0">
        <div className="lg:hidden glass sticky top-0 z-40 flex items-center justify-between px-4 h-14 border-b border-[var(--border-c)]">
          <Logo />
          <div className="flex items-center gap-1">
            {!user && (
              <button data-testid="login-btn-topbar" onClick={startGoogleLogin} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[var(--glow)] text-white text-xs font-semibold">
                <GoogleLogo size={13} weight="bold" /> Sign in
              </button>
            )}
            <button data-testid="mobile-menu-btn" onClick={() => setMobileOpen(true)} className="p-2 text-[var(--text)]">
              <List size={22} />
            </button>
          </div>
        </div>
        <Outlet />
      </main>

      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-50" data-testid="mobile-menu">
          <div className="absolute inset-0 bg-black/60" data-testid="mobile-menu-backdrop" onClick={() => setMobileOpen(false)} />
          <div className="absolute inset-y-0 right-0 w-[85%] max-w-[320px] bg-[var(--bg)] border-l border-[var(--border-c)] flex flex-col p-5">
            <div className="flex items-center justify-between mb-5">
              <Logo />
              <button data-testid="mobile-menu-close" onClick={() => setMobileOpen(false)} className="p-2 text-[var(--muted)]">
                <X size={20} weight="bold" />
              </button>
            </div>
            <div className="flex-1 min-h-0 overflow-y-auto -mx-1 px-1">
              <NavContent isAdmin={user?.isAdmin} onNavigate={() => setMobileOpen(false)} />
            </div>
            <AccountFooter user={user} logout={logout} theme={theme} toggleTheme={toggle} suffix="-mobile" />
          </div>
        </div>
      )}

      <nav className="lg:hidden fixed bottom-0 inset-x-0 z-40 glass border-t border-[var(--border-c)] h-16 grid grid-cols-4">
        {[
          { to: "/", icon: Wrench, label: "Tools", testId: "bottomnav-tools" },
          { to: "/dashboard", icon: SquaresFour, label: "Dashboard", testId: "bottomnav-dashboard" },
          { to: "/calendar", icon: CalendarBlank, label: "Calendar", testId: "bottomnav-calendar" },
          { to: "/scripts/new", icon: FileText, label: "Script", testId: "bottomnav-script" },
        ].map((i) => (
          <NavLink
            key={i.to}
            to={i.to}
            data-testid={i.testId}
            className={({ isActive }) =>
              `flex flex-col items-center justify-center gap-0.5 text-[10px] font-medium ${
                isActive ? "text-[var(--glow)]" : "text-[var(--muted)]"
              }`
            }
          >
            <i.icon size={20} weight="duotone" /> {i.label}
          </NavLink>
        ))}
      </nav>
    </div>
  );
};
